import { useCallback, useRef, useState } from 'react';
import { messageErreur, useSession } from './session.tsx';
import { corpsImprime, telechargerPdf, type DemandeImpression } from './impression.ts';

/**
 * Le bouton « Télécharger le PDF » d'un écran.
 *
 * Toutes les impressions arrivent en PDF : un fichier qu'on imprime, archive
 * ou transmet, sans la boîte d'impression ni la date et l'adresse que le
 * navigateur ajoute en marge.
 *
 * ── Pourquoi un crochet plutôt qu'un appel direct ───────────────────────────
 * Trois choses vont ensemble : ce qu'on imprime, l'état « en cours » — le
 * rendu prend une demi-seconde, et un bouton qui ne réagit pas se reclique —,
 * et le message quand cela échoue. Laisser chaque écran les recoudre
 * garantirait qu'un des huit oublie le troisième.
 */
export function useTelechargementPdf() {
  const { api } = useSession();
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const telecharger = useCallback(async (
    demande: Pick<DemandeImpression, 'titre' | 'orientation' | 'style' | 'sansPapier'> & { corps: string; classe?: string },
  ) => {
    setEnCours(true);
    setErreur(null);
    try {
      await telechargerPdf(api, demande);
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  }, [api]);

  return { telecharger, enCours, erreur, signaler: setErreur, effacerErreur: () => setErreur(null) };
}

/**
 * Le PDF de ce qu'affiche un écran : des fragments de la page, copiés et
 * transformés (voir `preparerPourImpression`).
 */
export function useImpression(
  options: Omit<DemandeImpression, 'sources' | 'ouvrir'> & {
    /**
     * Fragments à imprimer, cherchés dans la page au moment du clic.
     *
     * Un SÉLECTEUR et non une référence : l'écran de l'évaluation en a un par
     * domaine, leur nombre change avec la matière choisie, et tenir une liste
     * de références à jour ne servirait qu'à l'oublier.
     */
    selecteur: string;
  },
) {
  const pdf = useTelechargementPdf();
  // Les options changent à chaque rendu (titre calculé) : une référence évite
  // de recréer la fonction, et donc de faire clignoter le bouton.
  const dernieres = useRef(options);
  dernieres.current = options;

  const lancer = useCallback(() => {
    const { selecteur, ...reste } = dernieres.current;
    const sources = [...document.querySelectorAll<HTMLElement>(selecteur)];
    if (sources.length === 0) {
      pdf.signaler("Il n'y a rien à imprimer sur cet écran.");
      return;
    }
    // Le document est construit TOUT DE SUITE, sur la page telle qu'elle est :
    // certaines mesures se prennent sur l'écran affiché.
    const corps = corpsImprime({ ...reste, sources });
    void pdf.telecharger({ ...reste, corps });
  }, [pdf.telecharger, pdf.signaler]);

  return { lancer, enCours: pdf.enCours, erreur: pdf.erreur, effacerErreur: pdf.effacerErreur };
}
